import { useCallback } from 'react';
import { breakpoint } from '@carvana/ds-styles';
import LinkButton from '@carvana/ds-link-button';
import SummaryTile from './SummaryTile';
import SummaryTilesModal from './SummaryTilesModal';
import { HeroRightColumn, TilesContainer } from './SummaryTiles.styles';
import { LifecycleState, type VehicleSummary } from 'api/GraphQL/queries/getVehicleSummaries';
import { buildNavigationUrl } from 'shared/routes';
import { useMediaQuery } from 'shared/hooks/useMediaQuery';
import useAnalyticEvent from 'shared/hooks/useAnalyticEvent';

type SummaryTilesProps = {
  summaries: VehicleSummary[];
};

const sortByDesc = (a: VehicleSummary, b: VehicleSummary) =>
  b.expirationDate.localeCompare(a.expirationDate);

const SummaryTiles = ({ summaries }: SummaryTilesProps) => {
  const { recordActionClicked } = useAnalyticEvent();
  const isMobile = useMediaQuery(`(max-width: ${breakpoint.sm})`);
  const tileCount = summaries.length;
  const expired = summaries?.filter(e => e.lifecycleState === LifecycleState.Expired) || [];
  const active = summaries?.filter(e => e.lifecycleState !== LifecycleState.Expired) || [];
  const sortedTiles = active.concat(expired).sort(sortByDesc);

  const handleGetAnotherOfferClick = useCallback(async () => {
    await recordActionClicked(
      'Landing Page - Get an offer for another vehicle',
      'Get an offer for another vehicle link',
      undefined,
      true
    );
    const url = buildNavigationUrl({ baseUrl: '/sell-my-car/getoffer', includeReferrer: true });
    window.location.href = url;
  }, [recordActionClicked]);

  return (
    <HeroRightColumn>
      <TilesContainer>
        {tileCount < 3 ? (
          sortedTiles.map((summary, i) => (
            <SummaryTile
              key={summary.appraisalId}
              summary={summary}
              index={i}
              isMobile={isMobile}
              tileCount={tileCount}
              showTileActions={summary.lifecycleState === LifecycleState.Expired}
            />
          ))
        ) : (
          <>
            <SummaryTile
              key={sortedTiles[0].appraisalId}
              summary={sortedTiles[0]}
              index={0}
              isMobile={isMobile}
              tileCount={tileCount}
              showTileActions={sortedTiles[0].lifecycleState === LifecycleState.Expired}
            />
            <SummaryTilesModal key="summary-modal" summaries={sortedTiles.slice(1)} />
          </>
        )}
      </TilesContainer>
      <LinkButton variant="inverse" underline={false} onClick={handleGetAnotherOfferClick}>
        + Get an Offer for Another Vehicle
      </LinkButton>
    </HeroRightColumn>
  );
};

export default SummaryTiles;
